/* ===== Shared Utility Types: Literal Unions, Constants, Guards ===== */
/* Split from types.ts — leaf node, 0 imports from other shared modules */
// --- Error Codes ---
/** Error codes for i18n — server returns code, client displays localized message */
export const ErrorCodes = {
    // Page
    PAGE_NAME_INVALID: 'PAGE_NAME_INVALID',
    PAGE_NOT_FOUND: 'PAGE_NOT_FOUND',
    PAGE_LIMIT: 'PAGE_LIMIT',
    // Location
    LINK_NOT_FOUND: 'LINK_NOT_FOUND',
    TARGET_PAGE_NOT_FOUND: 'TARGET_PAGE_NOT_FOUND',
    GM_ONLY_NAVIGATION: 'GM_ONLY_NAVIGATION',
    NAVIGATE_REQUEST_NOT_FOUND: 'NAVIGATE_REQUEST_NOT_FOUND',
    // Token
    TOKEN_NOT_FOUND: 'TOKEN_NOT_FOUND',
    TOKEN_MOVE_FORBIDDEN: 'TOKEN_MOVE_FORBIDDEN',
    // Sheet
    SHEET_NOT_FOUND: 'SHEET_NOT_FOUND',
    SHEET_NAME_INVALID: 'SHEET_NAME_INVALID',
    // Alert
    ALERT_LIMIT: 'ALERT_LIMIT',
    ALERT_NAME_INVALID: 'ALERT_NAME_INVALID',
    ALERT_TRIGGER_LIMIT: 'ALERT_TRIGGER_LIMIT',
    // Pin
    PIN_NOT_FOUND: 'PIN_NOT_FOUND',
    PIN_LIMIT: 'PIN_LIMIT',
    // Journal
    JOURNAL_NAME_INVALID: 'JOURNAL_NAME_INVALID',
    // Text
    TEXT_NOT_FOUND: 'TEXT_NOT_FOUND',
    TEXT_LIMIT: 'TEXT_LIMIT',
    TEXT_INVALID: 'TEXT_INVALID',
    // Map Object
    MAP_OBJECT_NOT_FOUND: 'MAP_OBJECT_NOT_FOUND',
    MAP_OBJECT_LIMIT: 'MAP_OBJECT_LIMIT',
    MAP_OBJECT_INVALID: 'MAP_OBJECT_INVALID',
    // Wall
    WALL_NOT_FOUND: 'WALL_NOT_FOUND',
    WALL_LIMIT: 'WALL_LIMIT',
    WALL_INVALID: 'WALL_INVALID',
    WALL_NOT_DOOR: 'WALL_NOT_DOOR',
    WALL_CHAIN_MIN_POINTS: 'WALL_CHAIN_MIN_POINTS',
    WALL_AUTO_MANAGED: 'WALL_AUTO_MANAGED',
    // Generic
    LIMIT_EXCEEDED: 'LIMIT_EXCEEDED',
    NOT_FOUND: 'NOT_FOUND',
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
};
/** Array of valid GameSystemId values for runtime validation */
export const GAME_SYSTEM_IDS = ['dnd5e', 'pf2e', 'generic'];
/** Type guard: check if a value is a valid GameSystemId */
export function isValidGameSystem(value) {
    return typeof value === 'string' && GAME_SYSTEM_IDS.includes(value);
}
/** Convenience helper: check if session is D&D 5e */
export function isDnd5eSession(state) {
    return state.gameSystem === 'dnd5e';
}
/** Валидные значения PersonaEmotion (для runtime validation) */
export const PERSONA_EMOTIONS = [
    'neutral', 'interested', 'amused', 'impressed', 'concerned', 'nostalgic', 'bored',
];
export const DEFAULT_PLAYER_PERMISSIONS = {
    moveOtherTokens: false,
    drawOnMap: true,
    viewOtherSheets: 'basic',
    createNotes: true,
    useAi: true,
    addTokens: false,
};
// --- Resource Limits ---
export const LIMITS = {
    MAX_TOKENS_PER_PAGE: 300,
    MAX_DRAWINGS_PER_PAGE: 1000,
    MAX_FOG_REVEALS_PER_PAGE: 2000,
    MAX_FOG_BATCH_POINTS: 200,
    MAX_FOG_CLOSINGS_PER_PAGE: 2000,
    MAX_POINTS_PER_STROKE: 5000,
    MAX_SHEETS_PER_SESSION: 100,
    MAX_JOURNAL_ENTRIES_PER_SESSION: 200,
    MAX_LOCATION_LINKS_PER_PAGE: 50,
    PENDING_NAVIGATION_TTL_MS: 5 * 60 * 1000,
    // AI Context limits
    MAX_WORLD_DESCRIPTION_CHARS: 2000,
    MAX_HOUSE_RULES_CHARS: 1000,
    MAX_GM_INSTRUCTIONS_CHARS: 500,
    MAX_EXCLUDED_ENTITIES: 50,
    MAX_SECTION_PRIORITY: 10,
    MAX_BACKSTORY_CHARS: 1500,
    MAX_PERSONALITY_CHARS: 500,
    MAX_PLAYER_NOTES_CHARS: 500,
    MAX_SESSION_FOCUS_CHARS: 300,
    MAX_ACTIVE_NPCS: 10,
    MAX_SPOILER_GUARD: 20,
    // Annotation & Trigger limits
    MAX_ANNOTATIONS_PER_PAGE: 100,
    MAX_TRIGGERS_PER_PAGE: 20,
    MAX_TRIGGER_ACTIONS: 10,
    MAX_ANNOTATION_LABEL_CHARS: 100,
    MAX_ANNOTATION_DESCRIPTION_CHARS: 500,
    MAX_TRIGGER_LABEL_CHARS: 100,
    /** SG-001 — bound activeEffects array on a single sheet (DoS via repeated effect-add). */
    MAX_ACTIVE_EFFECTS_PER_SHEET: 100,
    /** SG-002 — bound rows in a single repeating section (DoS via repeated row-add). */
    MAX_REPEATING_ROWS_PER_SECTION: 50,
    /** BF-06 — polygon vertex cap shared by fog reveals / closings / trigger geometry. */
    MAX_POLYGON_POINTS: 500,
    /** BF-07 — tokenIds array cap on `all_tokens_dead` trigger condition. */
    MAX_CONDITION_TOKEN_IDS: 200,
    // Text limits
    MAX_TEXT_OBJECTS_PER_PAGE: 200,
    MAX_TEXT_LENGTH: 500,
    MAX_BATCH_DUPLICATE_TOKENS: 20,
    // Map Object limits
    MAX_MAP_OBJECTS_PER_PAGE: 200,
    MAX_MAP_OBJECT_LABEL_CHARS: 200,
    MAX_MAP_OBJECT_DESCRIPTION_CHARS: 2000,
    // Wall limits (Dynamic Lighting)
    MAX_WALLS_PER_PAGE: 1000,
    WALL_CHAIN_MIN_POINTS: 2,
    // Drawing selection limits
    MAX_SELECTED_STROKES: 100,
    // Batch action limits
    MAX_BATCH_ACTIONS: 20,
    MAX_BATCH_DELAY_MS: 2000,
    // Tactical & Location Navigation limits
    MAX_FORMATION_TOKENS: 20,
    MAX_SETUP_LOCATION_TOKENS: 10,
    // Lighting limits (Dynamic Lighting Advanced)
    MAX_LIGHT_SOURCES: 50,
    // Vision limits (Dynamic Lighting Advanced)
    MAX_VISION_TOKENS: 20,
    // Homebrew limits (Spec 049-B)
    MAX_HOMEBREW_PACKAGES_PER_USER: 50,
    MAX_HOMEBREW_ENTRIES_PER_PACKAGE: 200,
    MAX_HOMEBREW_PDF_SIZE_BYTES: 100 * 1024 * 1024, // 100 MB
    MAX_HOMEBREW_TEXT_LENGTH: 100_000,
    // Timeline limits (Spec 053)
    MAX_TIMELINE_EVENTS: 1000,
    MAX_CAMPAIGN_SESSIONS: 100,
    MAX_TIMELINE_TAGS: 20,
    MAX_TIMELINE_LINKS_PER_EVENT: 10,
    MAX_TIMELINE_TITLE_CHARS: 200,
    MAX_TIMELINE_DESCRIPTION_CHARS: 5000,
    MAX_CALENDAR_MONTHS: 30,
    MAX_CALENDAR_MONTH_DAYS: 100,
    MAX_CALENDAR_WEEK_DAYS: 14,
};
